import React from 'react';
import { AlertTriangle, Zap, Clock, ArrowRight, CalendarCheck } from 'lucide-react';

const weekdayLabels = [
  { short: 'Pzt', full: 'Pazartesi', weekend: false },
  { short: 'Sal', full: 'Salı', weekend: false },
  { short: 'Çar', full: 'Çarşamba', weekend: false },
  { short: 'Per', full: 'Perşembe', weekend: false },
  { short: 'Cum', full: 'Cuma', weekend: false },
  { short: 'Cmt', full: 'Cumartesi', weekend: true },
  { short: 'Paz', full: 'Pazar', weekend: true },
];

const formatMinutes = (minutes) => {
  if (!minutes) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}s ${m}dk` : `${h} sa`;
};

const isAbsentStatus = (status) =>
  status === 'Gelmedi' || status === 'Devamsız' || status === 'Gelmedi (Kısmi İzinli)';

// Hücre yüzey + kenarlık + üst vurgu çizgisi (göz yormayan, sınırları net kurumsal palet)
const getCellClasses = (record, isPadding = false) => {
  if (isPadding && !record) {
    return 'bg-slate-100/60 border-slate-200/70 text-slate-400 dark:bg-slate-950/60 dark:border-slate-800/70 dark:text-slate-600';
  }
  if (!record) {
    return 'bg-white border-slate-200/90 text-slate-700 hover:border-slate-300 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300 dark:hover:border-slate-700';
  }

  // Onaylı İzin
  if (record.isLeaveDay && !isAbsentStatus(record.status)) {
    return 'bg-white bg-gradient-to-b from-violet-50/70 to-white border-violet-200/90 border-t-[3px] border-t-violet-500/80 text-slate-800 hover:border-violet-300 dark:bg-slate-900 dark:from-violet-950/35 dark:to-slate-900 dark:border-slate-800 dark:border-t-violet-500/75 dark:text-slate-100 dark:hover:border-violet-800/70';
  }

  // Resmi Tatil (parlak/yanıp sönen animasyon yerine dingin kurumsal cyan vurgu)
  if (record.isHoliday) {
    return 'bg-white bg-gradient-to-b from-cyan-50/75 to-white border-cyan-200/90 border-t-[3px] border-t-cyan-500/85 text-slate-800 hover:border-cyan-300 dark:bg-slate-900 dark:from-cyan-950/35 dark:to-slate-900 dark:border-slate-800 dark:border-t-cyan-500/75 dark:text-slate-100 dark:hover:border-cyan-800/70';
  }

  // Hafta Tatili / Serbest Zaman
  if (record.isDayOff) {
    if (record.status === 'Serbest Zaman') {
      return 'bg-white bg-gradient-to-b from-teal-50/60 to-white border-teal-200/85 border-t-[3px] border-t-teal-500/75 text-slate-800 hover:border-teal-300 dark:bg-slate-900 dark:from-teal-950/30 dark:to-slate-900 dark:border-slate-800 dark:border-t-teal-500/70 dark:text-slate-200 dark:hover:border-teal-800/70';
    }
    return 'bg-slate-50/85 bg-gradient-to-b from-sky-50/55 to-slate-50/70 border-slate-200/95 border-t-[3px] border-t-sky-500/75 text-slate-700 hover:border-sky-300 dark:bg-slate-900 dark:from-sky-950/30 dark:to-slate-900 dark:border-slate-800 dark:border-t-sky-500/70 dark:text-slate-200 dark:hover:border-sky-800/70';
  }

  // Devamsızlık
  if (isAbsentStatus(record.status)) {
    return 'bg-white bg-gradient-to-b from-rose-50/75 to-white border-rose-200/90 border-t-[3px] border-t-rose-500/85 text-slate-800 hover:border-rose-300 dark:bg-slate-900 dark:from-rose-950/40 dark:to-slate-900 dark:border-slate-800 dark:border-t-rose-500/80 dark:text-slate-100 dark:hover:border-rose-800/70';
  }

  // Erken Çıkış / Geç Giriş
  if (record.hasEarlyExit || record.hasLateEntry) {
    return 'bg-white bg-gradient-to-b from-amber-50/65 to-white border-amber-200/90 border-t-[3px] border-t-amber-500/80 text-slate-800 hover:border-amber-300 dark:bg-slate-900 dark:from-amber-950/30 dark:to-slate-900 dark:border-slate-800 dark:border-t-amber-500/75 dark:text-slate-100 dark:hover:border-amber-800/70';
  }

  // Fazla Mesai
  if (record.overtimeMinutes > 0) {
    return 'bg-white bg-gradient-to-b from-emerald-50/65 to-white border-emerald-200/90 border-t-[3px] border-t-emerald-500/80 text-slate-800 hover:border-emerald-300 dark:bg-slate-900 dark:from-emerald-950/30 dark:to-slate-900 dark:border-slate-800 dark:border-t-emerald-500/75 dark:text-slate-100 dark:hover:border-emerald-800/70';
  }

  // Eksik Çalışma
  if (record.undertimeMinutes > 0) {
    return 'bg-white bg-gradient-to-b from-orange-50/65 to-white border-orange-200/90 border-t-[3px] border-t-orange-500/80 text-slate-800 hover:border-orange-300 dark:bg-slate-900 dark:from-orange-950/30 dark:to-slate-900 dark:border-slate-800 dark:border-t-orange-500/75 dark:text-slate-100 dark:hover:border-orange-800/70';
  }

  // Standart Normal Gün
  return 'bg-white border-slate-200/90 border-t-[3px] border-t-slate-300/80 text-slate-700 hover:border-slate-300 dark:bg-slate-900 dark:border-slate-800 dark:border-t-slate-700 dark:text-slate-200 dark:hover:border-slate-700';
};

// Sağ üst rozet (pastel-kontrast, göz yormayan)
const getBadge = (record) => {
  if (!record) return null;
  if (isAbsentStatus(record.status)) {
    return {
      label: 'YOK',
      cls: 'bg-rose-500/15 text-rose-700 ring-1 ring-inset ring-rose-500/30 dark:bg-rose-500/20 dark:text-rose-300 dark:ring-rose-400/30',
    };
  }
  if (record.isLeaveDay) {
    return {
      label: 'İZİN',
      cls: 'bg-violet-500/15 text-violet-700 ring-1 ring-inset ring-violet-500/25 dark:bg-violet-500/20 dark:text-violet-300 dark:ring-violet-400/30',
    };
  }
  if (record.isHoliday) {
    return {
      label: 'TATİL',
      cls: 'bg-cyan-500/15 text-cyan-800 ring-1 ring-inset ring-cyan-500/30 dark:bg-cyan-500/20 dark:text-cyan-300 dark:ring-cyan-400/30',
    };
  }
  if (record.isDayOff) {
    const isManual = record.dayOffSource === 2;
    if (record.status === 'Serbest Zaman') {
      return {
        label: 'SZ',
        cls: 'bg-teal-500/15 text-teal-700 ring-1 ring-inset ring-teal-500/25 dark:bg-teal-500/20 dark:text-teal-300 dark:ring-teal-400/30',
      };
    }
    return {
      label: isManual ? 'HT·M' : 'HT',
      cls: isManual
        ? 'bg-indigo-500/15 text-indigo-700 ring-1 ring-inset ring-indigo-500/30 dark:bg-indigo-500/25 dark:text-indigo-300 dark:ring-indigo-400/35'
        : 'bg-sky-500/15 text-sky-700 ring-1 ring-inset ring-sky-500/25 dark:bg-sky-500/20 dark:text-sky-300 dark:ring-sky-400/30',
    };
  }
  if (record.hasEarlyExit || record.status === 'Erken Çıkış') {
    return {
      label: 'EÇ',
      cls: 'bg-amber-500/15 text-amber-800 ring-1 ring-inset ring-amber-500/30 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-400/30',
    };
  }
  if (record.hasLateEntry || record.status === 'Geç Giriş') {
    return {
      label: 'GG',
      cls: 'bg-amber-500/15 text-amber-800 ring-1 ring-inset ring-amber-500/30 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-400/30',
    };
  }
  if (record.overtimeMinutes > 0) {
    return {
      label: 'FM',
      cls: 'bg-emerald-500/15 text-emerald-700 ring-1 ring-inset ring-emerald-500/25 dark:bg-emerald-500/20 dark:text-emerald-300 dark:ring-emerald-400/30',
    };
  }
  if (record.undertimeMinutes > 0) {
    return {
      label: 'EK',
      cls: 'bg-orange-500/15 text-orange-700 ring-1 ring-inset ring-orange-500/25 dark:bg-orange-500/20 dark:text-orange-300 dark:ring-orange-400/30',
    };
  }
  if (record.firstEntryTime || record.lastExitTime) {
    return {
      label: 'NRM',
      cls: 'bg-slate-500/10 text-slate-600 ring-1 ring-inset ring-slate-400/25 dark:bg-slate-700/50 dark:text-slate-300 dark:ring-slate-600',
    };
  }
  return null;
};

const LEGEND_ITEMS = [
  { code: 'NRM', label: 'Normal Mesai', dot: 'bg-slate-400 dark:bg-slate-500' },
  { code: 'FM', label: 'Fazla Mesai', dot: 'bg-emerald-500' },
  { code: 'EK', label: 'Eksik Çalışma', dot: 'bg-orange-500' },
  { code: 'GG / EÇ', label: 'Geç Giriş / Erken Çıkış', dot: 'bg-amber-500' },
  { code: 'YOK', label: 'Devamsızlık', dot: 'bg-rose-500' },
  { code: 'HT', label: 'Hafta Tatili', dot: 'bg-sky-500' },
  { code: 'İZİN', label: 'Onaylı İzin', dot: 'bg-violet-500' },
  { code: 'TATİL', label: 'Resmi Tatil', dot: 'bg-cyan-500' },
];

const AttendanceCalendar = ({
  month,
  year,
  data,
  weekSummaries = [],
  weeklyRight = 0,
  onDayClick,
  loading,
  onOpenWeekOffModal,
  onOpenQualityDrawer,
}) => {
  const firstDate = new Date(year, month - 1, 1);
  const offset = (firstDate.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();

  const cells = [];

  // Padding Start
  for (let i = 0; i < offset; i += 1) {
    const d = new Date(year, month - 1, 1);
    d.setDate(d.getDate() - (offset - i));
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const dateStr = `${d.getFullYear()}-${m}-${day}`;
    const dayData = data?.find((x) => x.date.split('T')[0] === dateStr);
    cells.push({ padding: true, day: d.getDate(), dateStr, data: dayData, key: `pad-start-${i}` });
  }

  // Current Month
  for (let i = 1; i <= daysInMonth; i += 1) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
    const dayData = data?.find((x) => x.date.split('T')[0] === dateStr);
    cells.push({ padding: false, day: i, dateStr, data: dayData, key: dateStr });
  }

  // Padding End
  const remainder = cells.length % 7;
  if (remainder !== 0) {
    for (let i = 0; i < 7 - remainder; i += 1) {
      const d = new Date(year, month, i + 1);
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const dateStr = `${d.getFullYear()}-${m}-${day}`;
      const dayData = data?.find((x) => x.date.split('T')[0] === dateStr);
      cells.push({ padding: true, day: d.getDate(), dateStr, data: dayData, key: `pad-end-${i}` });
    }
  }

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  return (
    <div>
      {/* Dış Takvim Çerçevesi */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-slate-100/80 shadow-inner dark:border-slate-800 dark:bg-slate-950/90">
        {/* Gün Başlıkları */}
        <div className="grid grid-cols-7 border-b border-slate-200/90 bg-white dark:border-slate-800 dark:bg-slate-900">
          {weekdayLabels.map((item) => (
            <div
              key={item.short}
              className={`border-r border-slate-200/60 px-3 py-2.5 text-center text-[11px] font-bold uppercase tracking-wider last:border-r-0 dark:border-slate-800/80 ${
                item.weekend
                  ? 'bg-slate-50/70 text-slate-500 dark:bg-slate-900/60 dark:text-slate-400'
                  : 'text-slate-600 dark:text-slate-300'
              }`}
            >
              {item.short}
            </div>
          ))}
        </div>

        {/* Takvim Hücre Izgarası — Her gün kendi net sınır çizgisine (border) ve ayrım boşluğuna sahip */}
        <div className="relative grid grid-cols-7 gap-2 p-2.5">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[2px] dark:bg-slate-950/75">
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-xl dark:border-slate-800 dark:bg-slate-900">
                <div className="h-8 w-8 animate-spin rounded-full border-3 border-indigo-600/20 border-t-indigo-600 dark:border-indigo-400/20 dark:border-t-indigo-400" />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 dark:text-slate-300">
                  Takvim Hazırlanıyor
                </span>
              </div>
            </div>
          )}

          {Array.from({ length: Math.ceil(cells.length / 7) }).map((_, rowIndex) => {
            const row = cells.slice(rowIndex * 7, (rowIndex + 1) * 7);
            const dataCells = row.filter((c) => !c.padding && c.data);
            const weekHasData = dataCells.length > 0;

            return (
              <React.Fragment key={`row-${rowIndex}`}>
                {row.map((cell) => {
                  if (cell.padding && !cell.data) {
                    return (
                      <div
                        key={cell.key}
                        className="min-h-[126px] rounded-xl border border-dashed border-slate-200/80 bg-slate-50/60 p-2.5 dark:border-slate-800/70 dark:bg-slate-900/35"
                      >
                        <span className="text-xs font-semibold text-slate-400 dark:text-slate-600">{cell.day}</span>
                      </div>
                    );
                  }

                  const record = cell.data;
                  const badge = record ? getBadge(record) : null;
                  const isToday = cell.dateStr === todayStr;
                  const displayDuration = record
                    ? formatMinutes(record.actualMinutes || record.actualWorkDurationMinutes || 0)
                    : null;

                  const hasQualityIssue =
                    record?.qualityStatus === 1 || record?.qualityStatus === 2;
                  const hasEntryOrExit = Boolean(record?.firstEntryTime || record?.lastExitTime);

                  return (
                    <button
                      key={cell.key}
                      type="button"
                      onClick={() => record && onDayClick(record.date)}
                      className={`group relative flex min-h-[126px] flex-col justify-between rounded-xl border p-2.5 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-150 ${getCellClasses(
                        record,
                        cell.padding
                      )} ${
                        record
                          ? 'cursor-pointer hover:-translate-y-[1px] hover:shadow-md dark:hover:shadow-black/40'
                          : 'cursor-default'
                      } ${
                        isToday
                          ? 'ring-2 ring-indigo-600/80 dark:ring-indigo-400/80'
                          : ''
                      } ${cell.padding ? 'opacity-55 hover:opacity-90' : ''}`}
                    >
                      {/* Üst Satır: Gün Numarası + Sağ Üst Rozet ve Kalite Uyarısı */}
                      <div className="flex w-full items-start justify-between gap-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-lg px-1.5 text-xs font-bold tabular-nums ${
                              isToday
                                ? 'bg-indigo-600 text-white shadow-sm dark:bg-indigo-500'
                                : cell.padding
                                  ? 'text-slate-400 dark:text-slate-500'
                                  : 'bg-slate-100/90 text-slate-800 dark:bg-slate-800/90 dark:text-slate-100'
                            }`}
                          >
                            {cell.day}
                          </span>
                        </div>

                        <div className="flex items-center gap-1">
                          {hasQualityIssue && (
                            <span
                              role={record?.puantajId && onOpenQualityDrawer ? 'button' : undefined}
                              title={
                                record?.qualityStatus === 2
                                  ? 'Kritik Puantaj Hatası — Akıllı Çözüm Merkezini Aç'
                                  : 'Puantaj Uyarısı — Akıllı Çözüm Merkezini Aç'
                              }
                              onClick={(e) => {
                                if (record?.puantajId && onOpenQualityDrawer) {
                                  e.stopPropagation();
                                  onOpenQualityDrawer(record.puantajId);
                                }
                              }}
                              className={`inline-flex h-5 w-5 items-center justify-center rounded-md transition-transform hover:scale-110 ${
                                record?.qualityStatus === 2
                                  ? 'bg-rose-500/15 text-rose-600 ring-1 ring-inset ring-rose-500/35 dark:bg-rose-500/25 dark:text-rose-300 dark:ring-rose-400/40'
                                  : 'bg-amber-500/15 text-amber-600 ring-1 ring-inset ring-amber-500/35 dark:bg-amber-500/25 dark:text-amber-300 dark:ring-amber-400/40'
                              }`}
                            >
                              <AlertTriangle size={11} />
                            </span>
                          )}

                          {badge && (
                            <span
                              className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold tracking-wide ${badge.cls}`}
                            >
                              {badge.label}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Orta Satır: Giriş - Çıkış Saatleri ve Vardiya Bilgisi */}
                      <div className="my-1.5 flex flex-col gap-1">
                        {hasEntryOrExit ? (
                          <div className="inline-flex w-fit items-center gap-1 rounded-md border border-slate-200/80 bg-slate-50/90 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-700 dark:border-slate-700/80 dark:bg-slate-800/80 dark:text-slate-200">
                            <span>
                              {record.firstEntryTime
                                ? new Date(record.firstEntryTime).toLocaleTimeString('tr-TR', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '—'}
                            </span>
                            <ArrowRight size={9} className="text-slate-400 dark:text-slate-500" />
                            <span>
                              {record.lastExitTime
                                ? new Date(record.lastExitTime).toLocaleTimeString('tr-TR', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '—'}
                            </span>
                          </div>
                        ) : null}

                        {record?.shiftCode ? (
                          <div className="truncate text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                            {record.shiftCode}
                          </div>
                        ) : record && !record.isLeaveDay && !record.isDayOff && !record.isHoliday && !hasEntryOrExit ? (
                          <span className="text-[10px] text-slate-400 dark:text-slate-600">Hareket yok</span>
                        ) : null}
                      </div>

                      {/* Alt Satır: Durum Açıklaması + Fiili Süre */}
                      <div className="flex items-end justify-between gap-1 border-t border-slate-100 pt-1.5 dark:border-slate-800/80">
                        <div className="truncate text-[10px] font-semibold leading-tight">
                          {record?.isLeaveDay && !isAbsentStatus(record?.status) && (
                            <span className="text-violet-700 dark:text-violet-300">Onaylı İzin</span>
                          )}
                          {record?.isHoliday && (
                            <span className="text-cyan-700 dark:text-cyan-300">Resmi Tatil</span>
                          )}
                          {record?.isDayOff && !record?.isLeaveDay && !record?.isHoliday && (
                            <span
                              className={
                                record?.status === 'Serbest Zaman'
                                  ? 'text-teal-700 dark:text-teal-300'
                                  : 'text-sky-700 dark:text-sky-300'
                              }
                            >
                              {record?.status === 'Serbest Zaman' ? 'Serbest Zaman' : 'Hafta Tatili'}
                            </span>
                          )}
                          {!record?.isDayOff && !record?.isHoliday && isAbsentStatus(record?.status) && (
                            <span className="text-rose-700 dark:text-rose-300">
                              {record?.status === 'Gelmedi (Kısmi İzinli)' ? 'Kısmi İzin / Yok' : 'Devamsızlık'}
                            </span>
                          )}
                          {record?.overtimeMinutes > 0 && !record?.isLeaveDay && !record?.isDayOff && (
                            <span className="inline-flex items-center gap-0.5 text-emerald-700 dark:text-emerald-300">
                              <Zap size={10} className="shrink-0" />+{formatMinutes(record.overtimeMinutes)}
                            </span>
                          )}
                          {record?.undertimeMinutes > 0 &&
                            !record?.isLeaveDay &&
                            !record?.isDayOff &&
                            !isAbsentStatus(record?.status) && (
                              <span className="text-orange-700 dark:text-orange-300">
                                -{formatMinutes(record.undertimeMinutes)} eksik
                              </span>
                            )}
                          {!record?.isLeaveDay &&
                            !record?.isHoliday &&
                            !record?.isDayOff &&
                            !isAbsentStatus(record?.status) &&
                            !(record?.overtimeMinutes > 0) &&
                            !(record?.undertimeMinutes > 0) &&
                            hasEntryOrExit && (
                              <span className="text-slate-500 dark:text-slate-400">Normal</span>
                            )}
                        </div>

                        {displayDuration && !record?.isDayOff && !record?.isLeaveDay && !record?.isHoliday && (
                          <div className="inline-flex shrink-0 items-center gap-0.5 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            <Clock size={9} className="text-slate-400 dark:text-slate-500" />
                            {displayDuration}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Haftalık HT Özeti Şeridi */}
                {weekHasData && weeklyRight > 0 && (
                  <div className="col-span-7 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200/90 bg-white px-4 py-2 text-xs shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex flex-wrap items-center gap-4 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                        <CalendarCheck size={13} />
                        Haftalık HT Özeti
                      </span>

                      {(() => {
                        const rowWeekStart = new Date(year, month - 1, 1);
                        rowWeekStart.setDate(rowWeekStart.getDate() - offset + rowIndex * 7);
                        const formatLocalDay = (dt) =>
                          `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(
                            dt.getDate()
                          ).padStart(2, '0')}`;
                        const rowWeekStartStr = formatLocalDay(rowWeekStart);

                        const ws = weekSummaries.find((s) => {
                          const sStr =
                            typeof s.weekStart === 'string' && s.weekStart.length >= 10
                              ? s.weekStart.substring(0, 10)
                              : formatLocalDay(new Date(s.weekStart));
                          return sStr === rowWeekStartStr;
                        });

                        const effectiveCount = ws
                          ? ws.effectiveAssignedCount
                          : dataCells.filter((c) => c.data.isDayOff).length;
                        const right = ws ? ws.weeklyRight : weeklyRight;
                        const remaining = right - effectiveCount;
                        const statusText =
                          ws?.status || (effectiveCount >= right ? 'Tamamlandı' : 'Devam Ediyor');

                        let statusCls = 'text-slate-500 dark:text-slate-400';
                        if (statusText === 'Tamamlandı')
                          statusCls = 'text-emerald-600 dark:text-emerald-400 font-bold';
                        else if (statusText === 'Aşım')
                          statusCls = 'text-rose-600 dark:text-rose-400 font-bold';
                        else if (statusText === 'Eksik')
                          statusCls = 'text-amber-600 dark:text-amber-400 font-bold';

                        return (
                          <>
                            <span>
                              Hak: <strong className="text-slate-900 dark:text-white">{right}</strong>
                            </span>
                            <span>
                              Atanan:{' '}
                              <strong
                                className={
                                  effectiveCount > 0
                                    ? 'text-slate-900 dark:text-white'
                                    : 'text-slate-400'
                                }
                              >
                                {effectiveCount}
                              </strong>
                            </span>
                            <span>
                              Kalan:{' '}
                              <strong
                                className={
                                  remaining > 0
                                    ? 'text-indigo-600 dark:text-indigo-400'
                                    : 'text-slate-400'
                                }
                              >
                                {remaining < 0 ? 0 : remaining}
                              </strong>
                            </span>
                            <span className="flex items-center gap-1">
                              Durum: <span className={statusCls}>{statusText}</span>
                            </span>
                          </>
                        );
                      })()}
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onOpenWeekOffModal) {
                          const weekStart = new Date(year, month - 1, 1);
                          weekStart.setDate(weekStart.getDate() - offset + rowIndex * 7);
                          onOpenWeekOffModal(weekStart);
                        }
                      }}
                      className="rounded-lg border border-indigo-200 bg-indigo-50/80 px-3 py-1 text-[11px] font-bold text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-800/70 dark:bg-indigo-950/60 dark:text-indigo-300 dark:hover:bg-indigo-900/60"
                    >
                      Tatili Yönet
                    </button>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Alt Renk ve Kısaltma Anahtarı (Legend) */}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-slate-600 dark:text-slate-400">
        {LEGEND_ITEMS.map((item) => (
          <div
            key={item.code}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200/80 bg-white px-2.5 py-1 shadow-[0_1px_2px_rgba(15,23,42,0.02)] dark:border-slate-800 dark:bg-slate-900"
          >
            <span className={`h-2 w-2 rounded-full ${item.dot}`} />
            <span className="font-bold text-slate-800 dark:text-slate-200">{item.code}:</span>
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttendanceCalendar;
